/**
 * CreativeLightbox — full-screen viewer for attached creatives (images + videos)
 * with prev/next arrows to step through up to N creatives. Videos play with native
 * controls; images render object-contain, and tall creatives (landing pages)
 * scroll at natural width instead of shrinking to fit. Escape or a backdrop click
 * closes; the Left/Right arrow keys navigate. Shared by the composer preview
 * (ChatFileUpload), the attachment tiles, the captured-image chip and the
 * questionnaire asset panels so every surface expands a creative the same way.
 *
 * Robustness: portaled to <body> (a transformed/animated
 * chat-message ancestor re-roots `position: fixed` and would trap the overlay
 * inside the thread column), dialog semantics, focus save/trap/restore, and a
 * body scroll lock while open.
 */
import { useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { usePrivateMedia } from '@/hooks/usePrivateMedia'
import api from '@/lib/api/client'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Download, X } from '@digital-twins/design-system/icons'
import { toast } from 'sonner'

import { youtubeEmbedUrl, youtubeVideoId } from '@digital-twins/shared-types'

import { isVideoCreativeLike } from './CreativeMedia'

const EXTENSION_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
}

/** A save-as name: the URL's own file name, else `creative.<ext>` from the MIME. */
export function creativeDownloadName(src: string, mimeType?: string): string {
  if (!src.startsWith('data:')) {
    try {
      const last = new URL(src).pathname.split('/').pop() ?? ''
      if (/^[\w.-]+\.[A-Za-z0-9]{2,5}$/.test(last)) {
        return last
      }
    } catch {
      /* Not a URL: fall back to the MIME. */
    }
  }
  const sniffed = mimeType ?? /^data:([^;,]+)/.exec(src)?.[1]
  const extension = sniffed ? EXTENSION_BY_MIME[sniffed.toLowerCase()] : undefined
  return `creative.${extension ?? 'bin'}`
}

/** Clicks a transient `<a>` for `href`; the caller owns any blob URL it made. */
function clickDownloadLink(href: string, filename: string, opensNewTab: boolean): void {
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  if (opensNewTab) {
    link.target = '_blank'
    link.rel = 'noopener'
  }
  document.body.appendChild(link)
  link.click()
  link.remove()
}

/**
 * Saves a creative through a blob URL, the `<a download>` pattern from
 * `ThreadSourceView`: a cross-origin href ignores `download`, a blob URL does
 * not. When the bytes cannot be fetched at all (a bucket that sends no CORS
 * header makes `fetch` throw), it falls back to a plain link to the URL, which
 * cross-origin may open the image in a new tab instead of saving it. Throws
 * when the server refuses the file or neither path can run; the caller shows
 * the toast.
 */
export async function downloadCreativeFile(src: string, mimeType?: string): Promise<void> {
  if (src.startsWith('/api/')) {
    const { data } = await api.get<Blob>(src.slice(4), { responseType: 'blob' })
    const objectUrl = URL.createObjectURL(data)
    clickDownloadLink(objectUrl, creativeDownloadName(src, mimeType || data.type), false)
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000)
    return
  }
  let response: Response
  try {
    response = await fetch(src)
  } catch (fetchError) {
    try {
      clickDownloadLink(src, creativeDownloadName(src, mimeType), true)
    } catch {
      throw fetchError
    }
    return
  }
  if (!response.ok) {
    throw new Error(`Download failed (${response.status})`)
  }
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  clickDownloadLink(objectUrl, creativeDownloadName(src, mimeType || blob.type || undefined), false)
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000)
}

export interface LightboxCreative {
  /** data: or http(s) URL for the creative. */
  src: string
  /** Optional MIME (e.g. 'video/mp4'); inferred from a data: src when absent. */
  mimeType?: string
}

interface CreativeLightboxProps {
  creatives: Array<LightboxCreative>
  index: number
  onIndex: (index: number) => void
  onClose: () => void
  /** Override the root test id (default 'creative-lightbox') so a surface with an
   *  established lightbox test id (e.g. the staging tray) keeps its selectors.
   *  Child testids derive from it: `${dataTestId}-close/-prev/-next/-counter`. */
  dataTestId?: string
}

export function CreativeLightbox({ creatives, index, onIndex, onClose, dataTestId = 'creative-lightbox' }: CreativeLightboxProps) {
  const { t } = useTranslation('app')
  const count = creatives.length
  const many = count > 1
  const clamped = Math.max(0, Math.min(index, count - 1))
  const current = creatives[clamped]
  const privateMedia = usePrivateMedia(current?.src)
  const youtubePreviewId = current ? youtubeVideoId(current.src) : null

  const closeRef = useRef<HTMLButtonElement | null>(null)
  const downloadRef = useRef<HTMLButtonElement | null>(null)
  const prevRef = useRef<HTMLButtonElement | null>(null)
  const nextRef = useRef<HTMLButtonElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const previouslyFocused = useRef<Element | null>(null)

  const go = useCallback(
    (dir: -1 | 1) => {
      if (count <= 1) return
      onIndex((clamped + dir + count) % count)
    },
    [clamped, count, onIndex],
  )

  useEffect(() => {
    previouslyFocused.current = document.activeElement
    closeRef.current?.focus()

    // Lock body scroll so the page behind doesn't scroll under the overlay (the overlay
    // itself owns the scroll for tall creatives). Restored on close.
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = prevOverflow
      if (previouslyFocused.current instanceof HTMLElement) {
        previouslyFocused.current.focus()
      }
    }
    // Mount/unmount only: the lock and focus bookkeeping must not re-run per index change.
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft') {
        go(-1)
      } else if (e.key === 'ArrowRight') {
        go(1)
      } else if (e.key === 'Tab') {
        // Focus trap across the dialog's controls in DOM order: close, prev, the media
        // when it is a `<video controls>` (natively focusable; keyboard/AT users need to
        // reach play/pause/seek/volume, especially since it autoplays muted), then next.
        // An image creative contributes no stop (images are not focusable).
        const focusable = [
          closeRef.current,
          downloadRef.current,
          prevRef.current,
          videoRef.current,
          iframeRef.current,
          nextRef.current,
        ].filter(
          (el): el is HTMLButtonElement | HTMLVideoElement | HTMLIFrameElement => el != null,
        )
        if (focusable.length === 0) return
        e.preventDefault()
        const active = document.activeElement
        const at = focusable.findIndex((el) => el === active)
        const delta = e.shiftKey ? -1 : 1
        const next = focusable[(Math.max(at, 0) + delta + focusable.length) % focusable.length]
        next.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, onClose])

  const onDownload = () => {
    if (!current) {
      return
    }
    downloadCreativeFile(current.src, current.mimeType).catch(() => {
      toast.error(t('library.preview.toastDownloadFailed'))
    })
  }

  if (!current) return null
  // Portal target check: SSR-safe (no document during server render).
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('chromeA11y.fullCreative')}
      onClick={onClose}
      data-testid={dataTestId}
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/70 p-6"
    >
      <button
        ref={closeRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose() }}
        aria-label={t('chromeA11y.closeCreative')}
        data-testid={`${dataTestId}-close`}
        className="fixed right-4 top-4 z-[101] rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        <X className="h-5 w-5" />
      </button>

      {/* A YouTube embed has no file to save. */}
      {youtubePreviewId ? null : (
        <button
          ref={downloadRef}
          type="button"
          onClick={(e) => { e.stopPropagation(); onDownload() }}
          aria-label={t('threadImage.download')}
          data-testid={`${dataTestId}-download`}
          className="fixed right-16 top-4 z-[101] rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <Download className="h-5 w-5" />
        </button>
      )}

      {many && (
        <button
          ref={prevRef}
          type="button"
          onClick={(e) => { e.stopPropagation(); go(-1) }}
          aria-label={t('chromeA11y.prevCreative')}
          data-testid={`${dataTestId}-prev`}
          className="fixed left-4 top-1/2 z-[101] -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      {/* Clicking the media must NOT close the overlay — only the backdrop, X, or Escape do.
          items-start + overflow-y-auto on the root: a tall LP creative scrolls at natural
          width; `my-auto` keeps a short one vertically centered. */}
      <div onClick={(e) => e.stopPropagation()} className="my-auto flex max-w-[90vw] items-center justify-center">
        {youtubePreviewId ? (
          <iframe
            ref={iframeRef}
            title={t('chatChrome.youtubeVideoAlt')}
            src={youtubeEmbedUrl(youtubePreviewId)}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            tabIndex={0}
            data-testid={`${dataTestId}-youtube`}
            className="aspect-video h-[min(90vh,56.25vw)] w-[min(90vw,160vh)] max-w-[90vw] rounded-lg shadow-2xl"
          />
        ) : isVideoCreativeLike(current) ? (
          <video
            key={clamped}
            ref={videoRef}
            src={privateMedia.src ?? undefined}
            controls
            autoPlay
            muted
            playsInline
            // Explicit tab-order membership: a `controls` video is natively focusable in
            // browsers, but making it explicit keeps the trap's video stop reliable
            // everywhere (and testable): keyboard users must reach play/pause/unmute.
            tabIndex={0}
            aria-label={t('chatChrome.fullSizeVideoCreativeAria')}
            data-testid={`${dataTestId}-video`}
            className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-2xl"
          />
        ) : (
          <img
            src={privateMedia.src ?? undefined}
            alt={t('chatChrome.fullSizeCreativeAlt')}
            data-testid={`${dataTestId}-image`}
            className="h-auto w-auto max-w-[90vw] rounded-lg object-contain shadow-2xl"
          />
        )}
      </div>

      {many && (
        <button
          ref={nextRef}
          type="button"
          onClick={(e) => { e.stopPropagation(); go(1) }}
          aria-label={t('chromeA11y.nextCreative')}
          data-testid={`${dataTestId}-next`}
          className="fixed right-4 top-1/2 z-[101] -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}

      {many && (
        <span
          data-testid={`${dataTestId}-counter`}
          className="fixed bottom-4 left-1/2 z-[101] -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-white"
        >
          {t('chatChrome.creativeCounter', { current: clamped + 1, count })}
        </span>
      )}
    </div>,
    document.body,
  )
}

export default CreativeLightbox
